import React from "react";
import clsx from "clsx";
import { useTheme } from "@mui/material/styles";
import Skeleton from "@mui/material/Skeleton";
import Timeline from "@mui/lab/Timeline";
import TimelineItem from "@mui/lab/TimelineItem";
import TimelineSeparator from "@mui/lab/TimelineSeparator";
import TimelineConnector from "@mui/lab/TimelineConnector";
import TimelineContent from "@mui/lab/TimelineContent";
import TimelineOppositeContent from "@mui/lab/TimelineOppositeContent";
import Typography from "@mui/material/Typography";
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import { GROUP_BY_TIME } from "./importTimeline";
import useClasses from "../useClasses";
import { useTranslation } from "react-i18next";

const oppositeContentWidth = 70;
const timelineSeparatorWidth = 47;

const styles = (theme) => ({
  timeline: {
    width: "100%",
    padding: 0,
  },
  paddingTimeline: {
    padding: theme.spacing(3, 0, 0, 0),
  },
  oppositeContent: {
    minWidth: oppositeContentWidth,
    maxWidth: oppositeContentWidth,
    paddingBottom: theme.spacing(2),
    flex: 0,
    padding: theme.spacing(1),
  },
  noPaddingTop: {
    paddingTop: theme.spacing(0),
  },
  cTimelineContent: {
    paddingTop: theme.spacing(0),
    paddingLeft: "8px",
  },
  cTimelineSeparator: {
    maxWidth: timelineSeparatorWidth,
    minWidth: timelineSeparatorWidth,
  },
  paper: {
    padding: "0px 16px",
  },
  cTimelineOppositeContent: {
    display: "flex",
    minWidth: oppositeContentWidth,
    maxWidth: oppositeContentWidth,
    padding: theme.spacing(1),
    minHeight: 0,
  },
  card: {
    padding: 8,
    borderRadius: 0,
    height: "100%",
    width: "100%",
    borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
  },
});

/* const creations = [
  { parent: 0, children: [1, 2] },
  { parent: 1, children: [1, 2] },
  { parent: 3, children: [1, 2] },
]; */

const creations = [{ parent: 0, children: [1, 2, 3, 4, 5] }];

function TimelineHeader() {
  const { t } = useTranslation();
  return (
    <div>
      <Typography variant="h6">{t("timeline.subtitle")}</Typography>
      <Divider />
    </div>
  );
}

function DateItem() {
  const classes = useClasses(styles);
  const theme = useTheme();
  return (
    <TimelineItem>
      <TimelineOppositeContent className={classes.oppositeContent}>
        <div>
          <Skeleton
            variant="rectangular"
            width={theme.spacing(4)}
            style={{
              marginBottom: 4,
              borderRadius: theme.shape.borderRadius,
            }}
          />
          <Skeleton
            variant="rectangular"
            width={theme.spacing(3)}
            style={{ borderRadius: theme.shape.borderRadius }}
          />
        </div>
      </TimelineOppositeContent>
      <TimelineSeparator className={classes.cTimelineSeparator}>
        <Skeleton
          variant="rectangular"
          width={theme.spacing(4)}
          height={theme.spacing(4)}
          style={{ borderRadius: theme.shape.borderRadius }}
        />
        <TimelineConnector />
      </TimelineSeparator>
      <TimelineContent className={classes.cTimelineContent}></TimelineContent>
    </TimelineItem>
  );
}

function EventItem({ isFirstEventItem, isLastEventItem }) {
  const classes = useClasses(styles);
  const theme = useTheme();
  return (
    <TimelineItem style={{ minHeight: 0 }}>
      <TimelineOppositeContent
        className={clsx(classes.cTimelineOppositeContent, {})}
      ></TimelineOppositeContent>
      <TimelineSeparator className={classes.cTimelineSeparator}>
        <Skeleton
          variant="circular"
          width={theme.spacing(4)}
          height={theme.spacing(4)}
        />
        <TimelineConnector />
      </TimelineSeparator>
      <TimelineContent
        className={classes.paper}
        style={isLastEventItem ? { paddingBottom: "100px" } : {}}
      >
        <Card
          className={classes.card}
          style={
            isFirstEventItem && isLastEventItem
              ? {
                  borderRadius: theme.shape.borderRadius,
                  borderBottom: "0px solid rgba(0, 0, 0, 0.12)",
                }
              : isFirstEventItem
              ? {
                  borderTopLeftRadius: theme.shape.borderRadius,
                  borderTopRightRadius: theme.shape.borderRadius,
                }
              : isLastEventItem
              ? {
                  borderBottomLeftRadius: theme.shape.borderRadius,
                  borderBottomRightRadius: theme.shape.borderRadius,
                  borderBottom: "0px solid rgba(0, 0, 0, 0.12)",
                }
              : {}
          }
          elevation={1}
        >
          <Typography variant="subtitle2">
            <Skeleton animation="wave" height={16} width={theme.spacing(16)} />
          </Typography>

          <Typography variant="body2">
            <Skeleton animation="wave" height={16} width={theme.spacing(8)} />
          </Typography>
        </Card>
      </TimelineContent>
    </TimelineItem>
  );
}

export default function LoadingTimeline({ filterOnView, isScrolling = false }) {
  const classes = useClasses(styles);
  return (
    <div>
      {!isScrolling ? <TimelineHeader /> : null}
      <Timeline
        align="left"
        className={clsx(classes.timeline, {
          [classes.paddingTimeline]: !isScrolling,
        })}
      >
        {creations.map((creation, index) => {
          return (
            <div key={index}>
              {filterOnView !== GROUP_BY_TIME ? <DateItem /> : null}
              {creation.children.map((child, i) => {
                const isLastEventItem = i + 1 === creation.children.length;
                const isFirstEventItem =
                  filterOnView === GROUP_BY_TIME ? false : i === 0;
                return (
                  <EventItem
                    key={i}
                    isFirstEventItem={isFirstEventItem}
                    isLastEventItem={isLastEventItem}
                  />
                );
              })}
            </div>
          );
        })}
      </Timeline>
    </div>
  );
}
